import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { clientInvestSdk } from "@utils/clientInvestSdk";
import { logException } from "@helpers/logException/logException";
import {
  EISFundInvestment,
  EISFundInvestmentResponse,
  EISFundInvestmentSearchRequest,
} from "@ifgengineering/client-invest-sdk";
import EisFundActionTypes, {
  FetchEisFundInvestmentsPayload,
  LoadMoreEisFundInvestmentsPayload,
  EisFundFulfilledPayload,
  EisFundRejectedPayload,
  PaginationState,
} from "./eisFund.types";
import {
  EisFundStartup,
  EisFundDealDetails,
  InvestmentStatus,
  TaxBenefit,
  AccountType,
  StatusLabel,
  Certificate,
  EisFundEntity,
} from "@components/EisFund/types";

// ============================================================================
// Filter Key Generation
// ============================================================================

/**
 * Generate a filter key for cache validation.
 * When filters change, we need to refetch the data.
 */
export const generateFilterKey = (
  dealName?: string,
  taxBenefit?: TaxBenefit | "all",
  statusLabelNames?: InvestmentStatus[]
): string => {
  return JSON.stringify({
    dealName: dealName?.trim() || "",
    taxBenefit: taxBenefit || "all",
    statusLabelNames: statusLabelNames?.sort() || [],
  });
};

// ============================================================================
// API Parameter Helpers
// ============================================================================

/**
 * Convert taxBenefit filter to hasTaxBenefit boolean for API
 */
const getHasTaxBenefit = (
  taxBenefitFilter: TaxBenefit | "all" | undefined
): boolean | undefined => {
  if (!taxBenefitFilter || taxBenefitFilter === "all") {
    return undefined;
  }
  if (taxBenefitFilter === "None") {
    return false;
  }
  return true;
};

// ============================================================================
// Response Transformation
// ============================================================================

/**
 * Transform EISFundInvestmentResponse to EisFundStartup[] for UI display.
 */
function transformApiResponse(
  response: EISFundInvestmentResponse
): EisFundStartup[] {
  const items = response.data;

  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item: EISFundInvestment) => {
    // SDK types don't expose all deal fields; cast needed until SDK is updated
    const deal = item.deal as {
      name?: string;
      logoUrl?: string;
      website?: string;
      taxBenefit?: string;
      description?: string;
      summary?: string;
      videoUrl?: string;
      shariaComplianceSummary?: string;
      twitter?: string;
      linkedin?: string;
      companiesHouseUrl?: string;
    };

    // Extract deal details for the detail page
    const dealDetails: EisFundDealDetails = {
      description: deal.description || undefined,
      summary: deal.summary || undefined,
      videoUrl: deal.videoUrl || undefined,
      shariaComplianceSummary: deal.shariaComplianceSummary || undefined,
      twitter: deal.twitter || undefined,
      linkedin: deal.linkedin || undefined,
      companiesHouseUrl: deal.companiesHouseUrl || undefined,
    };

    return {
      id: item.investmentId,
      name: deal.name || "Unknown",
      logoUrl: deal.logoUrl || undefined,
      externalUrl: deal.website || undefined,
      amountInvested: item.amount || 0,
      taxBenefit: mapTaxBenefit(deal.taxBenefit),
      statusLabel: mapStatusLabel(item.statusLabel),
      certificate: mapCertificate(item.certificate),
      accountType: getAccountType(item),
      entity: mapEntity(item.entity),
      currency: "GBP",
      dealDetails,
      deployedAt: (item as EISFundInvestment & { deployedAt?: string })
        .deployedAt,
    };
  });
}

/**
 * Map tax benefit string to TaxBenefit type
 */
function mapTaxBenefit(taxBenefit: string | null | undefined): TaxBenefit {
  if (taxBenefit === "SEIS") return "SEIS";
  if (taxBenefit === "EIS") return "EIS";
  if (taxBenefit === "EIS/SEIS") return "EIS/SEIS";
  return "None";
}

/**
 * Map API status label to our StatusLabel type.
 */
function mapStatusLabel(
  apiStatusLabel: EISFundInvestment["statusLabel"]
): StatusLabel | null {
  if (!apiStatusLabel || !apiStatusLabel.name) {
    return null;
  }

  return {
    name: apiStatusLabel.name,
    textColor: apiStatusLabel.textColor || "#6B7280",
    bgColor: apiStatusLabel.bgColor || "#F3F4F6",
  };
}

/**
 * Map certificate from API.
 * If null, certificate is pending.
 */
function mapCertificate(
  apiCertificate: EISFundInvestment["certificate"]
): Certificate | null {
  if (!apiCertificate) {
    return null;
  }

  return {
    id: apiCertificate.id,
    name: apiCertificate.name,
    url: apiCertificate.url,
    tag: apiCertificate.tag,
  };
}

/**
 * Map entity from SDK type to UI type
 */
function mapEntity(
  apiEntity: EISFundInvestment["entity"]
): EisFundEntity | null {
  if (!apiEntity) {
    return null;
  }

  return {
    id: apiEntity.id,
    name: apiEntity.name,
    status: apiEntity.status,
  };
}

/**
 * Determine account type from investment data
 */
function getAccountType(item: EISFundInvestment): AccountType {
  // isIfIsa field may be present but not in SDK types yet
  if ((item as EISFundInvestment & { isIfIsa?: boolean }).isIfIsa)
    return "IF-ISA";
  if (item.entity) return "Entity";
  return "Personal";
}

/**
 * Extract pagination from EISFundInvestmentResponse
 */
function extractPagination(
  response: EISFundInvestmentResponse,
  requestedPageSize: number
): PaginationState {
  const currentPage = response.currentPage || 1;
  const totalCount = response.totalCount || 0;
  const totalPages =
    response.totalPages || Math.ceil(totalCount / requestedPageSize) || 1;
  const pageSize = response.pageSize || requestedPageSize;

  return {
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage: currentPage < totalPages,
  };
}

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch EIS Fund investments (initial load or filter change)
 */
export const fetchEisFundInvestments = createAsyncThunk<
  EisFundFulfilledPayload,
  FetchEisFundInvestmentsPayload,
  { rejectValue: EisFundRejectedPayload }
>(
  EisFundActionTypes.FETCH_EIS_FUND_INVESTMENTS,
  async (payload, { rejectWithValue }) => {
    const {
      dealId,
      commitmentId,
      dealName,
      taxBenefit,
      statusLabelNames,
      pageSize = 20,
    } = payload;

    try {
      const params: EISFundInvestmentSearchRequest = {
        page: 1,
        pageSize,
      };

      if (dealName && dealName.trim()) {
        params.dealName = dealName.trim();
      }

      const hasTaxBenefitValue = getHasTaxBenefit(taxBenefit);
      if (hasTaxBenefitValue !== undefined) {
        params.hasTaxBenefit = hasTaxBenefitValue;
      }

      if (statusLabelNames && statusLabelNames.length > 0) {
        params.statusLabelNames = statusLabelNames;
      }

      const response = await clientInvestSdk.getEISFundInvestments(
        commitmentId,
        params
      );

      const responseData = response.data as EISFundInvestmentResponse;
      const investments = transformApiResponse(responseData);
      const pagination = extractPagination(responseData, pageSize);

      return {
        dealId,
        commitmentId,
        investments,
        pagination,
        filterKey: generateFilterKey(dealName, taxBenefit, statusLabelNames),
      };
    } catch (error) {
      const axiosStatus = (error as { response?: { status?: number } })
        ?.response?.status;

      // Avoid reporting expected auth/validation failures to Sentry (noise).
      // These are handled by the auth flow / UI.
      if (axiosStatus !== undefined && axiosStatus < 500) {
        return rejectWithValue({
          dealId,
          commitmentId,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      logException({
        tag: "fetchEisFundInvestments",
        exception: error,
        message: "Failed to fetch EIS Fund investments",
      });
      return rejectWithValue({
        dealId,
        commitmentId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
);

/**
 * Load more EIS Fund investments (pagination)
 */
export const loadMoreEisFundInvestments = createAsyncThunk<
  EisFundFulfilledPayload,
  LoadMoreEisFundInvestmentsPayload,
  { rejectValue: EisFundRejectedPayload }
>(
  EisFundActionTypes.LOAD_MORE_EIS_FUND_INVESTMENTS,
  async (payload, { rejectWithValue }) => {
    const {
      dealId,
      commitmentId,
      dealName,
      taxBenefit,
      statusLabelNames,
      pageSize = 20,
      page,
    } = payload;

    try {
      const params: EISFundInvestmentSearchRequest = {
        page,
        pageSize,
      };

      if (dealName && dealName.trim()) {
        params.dealName = dealName.trim();
      }

      const hasTaxBenefitValue = getHasTaxBenefit(taxBenefit);
      if (hasTaxBenefitValue !== undefined) {
        params.hasTaxBenefit = hasTaxBenefitValue;
      }

      if (statusLabelNames && statusLabelNames.length > 0) {
        params.statusLabelNames = statusLabelNames;
      }

      const response = await clientInvestSdk.getEISFundInvestments(
        commitmentId,
        params
      );

      const responseData = response.data as EISFundInvestmentResponse;
      const investments = transformApiResponse(responseData);
      const pagination = extractPagination(responseData, pageSize);

      return {
        dealId,
        commitmentId,
        investments,
        pagination,
        filterKey: generateFilterKey(dealName, taxBenefit, statusLabelNames),
      };
    } catch (error) {
      const axiosStatus = (error as { response?: { status?: number } })
        ?.response?.status;

      // Avoid reporting expected auth/validation failures to Sentry (noise).
      // These are handled by the auth flow / UI.
      if (axiosStatus !== undefined && axiosStatus < 500) {
        return rejectWithValue({
          dealId,
          commitmentId,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      logException({
        tag: "loadMoreEisFundInvestments",
        exception: error,
        message: "Failed to load more EIS Fund investments",
      });
      return rejectWithValue({
        dealId,
        commitmentId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
);

// ============================================================================
// Plain Actions
// ============================================================================

/**
 * Clear cache for a specific deal/commitment or all
 */
export const clearEisFundCache = createAction<{
  dealId?: string;
  commitmentId?: string;
} | void>(EisFundActionTypes.CLEAR_EIS_FUND_CACHE);
